import { DeleteOutlined, EditOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Divider, Flex, Table } from 'antd'
import { TableColumnsType } from 'antd/lib'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  useNotificationsServiceDeleteTemplateEndpoint,
  useNotificationsServiceGetTemplatesListEndpoint,
  useNotificationsServiceGetTemplatesListEndpointKey
} from 'src/api/queries'
import {
  PageBreadcrumbs,
  Paragraph,
  SearchBar,
  Select,
  TableActionButtons,
  message
} from 'src/components'
import { useTablePagination } from 'src/hooks'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { getOrderingDirection, normalizeOrdering } from 'src/utils'

import { useStyles } from '../styles'

import { useConfig } from './config'

export const NotificationTemplatesPage = () => {
  const { t } = useLocales()
  const { styles } = useStyles()
  const pagination = useTablePagination()
  const navigate = useNavigate()
  const { options } = useConfig()

  const [search, setSearch] = useState('')
  const [ordering, setOrdering] = useState('')

  const queryClient = useQueryClient()

  const queryParams = {
    pageSize: Number(pagination.pageSize),
    pageNumber: Number(pagination.current),
    ...(!!search && {
      searchTerm: search
    }),
    ...(!!ordering && {
      sortBy: normalizeOrdering(ordering),
      sortDirection: getOrderingDirection(ordering)
    })
  }

  const { data, isLoading } =
    useNotificationsServiceGetTemplatesListEndpoint(queryParams)

  const { mutate, isPending } = useNotificationsServiceDeleteTemplateEndpoint({
    onSuccess: () => onMutationSuccess()
  })

  const getItems = useMemo(
    () => (id: string) => {
      return [
        {
          key: 'edit',
          label: t('common.edit'),
          icon: <EditOutlined />,
          onClick: () => navigate(paths.notificationTemplates.edit(String(id)))
        },
        {
          key: 'delete',
          label: t('common.delete'),
          icon: <DeleteOutlined />,
          danger: true,
          onClick: () => mutate({ id })
        }
      ]
    },
    [t]
  )

  const columns = useMemo(
    (): TableColumnsType => [
      {
        key: 'name',
        fixed: 'left',
        title: t('notification_templates.name'),
        dataIndex: 'name'
      },
      {
        key: 'event',
        title: t('notification_templates.event'),
        dataIndex: 'eventName'
      },
      {
        key: 'chanel',
        title: t('notification_templates.chanel'),
        dataIndex: 'channelName'
      },
      {
        key: 'roles',
        title: t('notification_templates.roles'),
        render: ({ destinationRoles }) => (
          <Paragraph weight={400} ellipsis={{ rows: 2 }}>
            {destinationRoles?.map((role: string, index: number) => (
              <div key={index}>{role}</div>
            ))}
          </Paragraph>
        )
      },
      {
        key: 'content',
        title: t('notification_templates.content'),

        render: ({ contents }) => (
          <div
            className={styles.oneRow}
            dangerouslySetInnerHTML={{ __html: contents }}
          />
        )
      },
      {
        key: 'actions',
        fixed: 'right',
        render: ({ id }) => <TableActionButtons items={getItems(id)} />
      }
    ],
    [t]
  )

  const onCreate = () => {
    navigate(paths.notificationTemplates.create)
  }

  const onMutationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: [useNotificationsServiceGetTemplatesListEndpointKey]
    })
    message.success(t('notification_templates.success_delete'))
  }

  return (
    <PageBreadcrumbs
      title={t('menu.notification_templates')}
      items={[{ title: t('menu.notification_templates') }]}
    >
      <Flex vertical>
        <Flex justify="space-between">
          <SearchBar
            onSearch={(val) => setSearch(val)}
            maxWidth={260}
            placeholder={t('notification_templates.search_placeholder')}
          />

          <Flex gap={8}>
            <Select options={options} onSelect={(opt) => setOrdering(opt)} />

            <Button type="primary" onClick={onCreate}>
              {t('notification_templates.create')}
            </Button>
          </Flex>
        </Flex>

        <Divider />
        <Table
          rowKey="name"
          scroll={{ x: 'max-content' }}
          columns={columns}
          loading={isLoading || isPending}
          dataSource={data?.data}
          pagination={{
            ...pagination,
            total: data?.totalCount
          }}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
